import type {
  InvestigationSourceTypes,
  InvestigationStateTypes,
  JourneyApplicationEventTypes,
  JourneyApplicationStateTypes,
  JourneyTypes,
} from '@usealloy/typegen';

import type { Application, JourneyStep } from '../application-review/redux/types';
import type { InvestigationDocumentResponse, TagEvidenceResponse } from './redux/types';
import type { IconName, Option as SelectOption } from '@usealloy/component-library';
import type { ENTITY_TYPES } from '~/types/entity';

type InvestigationEntityType =
  | typeof ENTITY_TYPES.person
  | typeof ENTITY_TYPES.business
  | typeof ENTITY_TYPES.entity_group;

export type InvestigationReview = {
  newInvestigationState: InvestigationStateTypes;
  newInvestigationStatus?: string;
  note?: string;
  outcome?: string;
  outcomeIcon?: IconName;
  outcomeColor?: string;
  previousInvestigationState: InvestigationStateTypes;
  reason?: string;
  reviewedAt: number;
  reviewedBy: InvestigationAgent;
  uiReviewToken: string;
  investigationStatusColor?: string;
  investigationStatusIcon?: IconName;
};

export type Investigation = {
  investigationCreatedAt: number;
  investigationClosedAt?: number;
  investigationArchivedAt?: number;
  investigationDocuments: InvestigationDocumentResponse[];
  entities: InvestigationEntityMap;
  investigationAssignedAgent?: InvestigationAgent;
  investigationEvents: InvestigationEvent[];
  investigationEvidences: InvestigationEvidences;
  investigationName?: string;
  investigationNotes: InvestigationNote[];
  investigationOutcome?: string;
  investigationOutcomeIcon?: IconName;
  investigationOutcomeColor?: string;
  investigationReason?: string;
  investigationReviews: InvestigationReview[];
  investigationSource: InvestigationSourceTypes;
  investigationState: InvestigationStateTypes;
  investigationStatus?: string;
  investigationStatusIcon?: IconName;
  investigationStatusColor?: string;
  investigationSummary?: InvestigationSummary;
  // All tags for the Investigation, from Alerts and manually added Evidences
  investigationTags: InvestigationTag[];
  investigationToken: string;
  investigationType: string;
  investigationUpdatedAt: number;
  journeyApplicationTokens: string[];
  investigationIsSandbox: boolean;
  numEntityGroupMembers: number;
  isGroupInvestigation: boolean;
};

type Tag = {
  color: string;
  isOutcomeReason: boolean;
  tagName: string;
};

export interface AlertEvent {
  createdAt: number;
  data: {
    actionName?: string;
    actionVersion?: number;
    outcomeReasons?: Tag[];
    tags?: Tag[];
    workflowName?: string;
    workflowVersion?: number;
  };
  status: string;
  type: string;
  updatedAt: number;
}

export interface InvestigationAlert {
  assignee?: string;
  createdAt: number;
  events: AlertEvent[];
  journeyApplicationToken: string;
  journeyName: string;
  journeyVersion: number;
  status: string;
  type: string;
  updatedAt: number;
}

export type OutcomeOptionsByReviewNodeId = {
  [nodeId: string]: {
    id: number | string;
    value: string;
  }[];
};

export type ReasonOptionsByOutcomeId = {
  [outcomeId: number]: {
    defaultNote?: string;
    id: number;
    value: string;
  }[];
};

export type CompletedAlertReview = {
  alertManualReviewToken?: string;
  note: string;
  outcome: string;
  reason: string;
  reviewedAt: string;
  reviewedBy?: InvestigationAgent;
};

export type AlertManualReview = {
  alertManualReviewToken: string;
  createdAt: string;
  entityApplicationToken: string;
  entityToken?: string;
  journeyApplicationEventToken: string;
  journeyApplicationToken: string;
  journeyType: JourneyTypes;
  name: string;
  nodeId: string;
  nodeType: string;
  note: string | null;
  outcome: string | null;
  reason: string | null;
  reviewedAt: string | null;
  reviewedBy: InvestigationAgent | null;
  status: string;
  timestamp: number;
  type: JourneyApplicationEventTypes;
};

export type AlertJourneyStep = JourneyStep & {
  review?: AlertManualReview;
  reviews?: AlertManualReview[];
};

export type EvaluationTag = {
  tagName: string;
  isCaseCreating: boolean;
  isOutcomeReason: boolean;
  tagColor: string;
  suppressionInterval?: object;
  isAssociatedToCases: boolean;
  isAdverseActionReason: boolean;
  isJourneyAppCreating: boolean;
  description: string;
};

export type EvaluationsByToken = {
  [evaluationToken: string]: {
    evaluationToken: string;
    eventRequestToken: string;
    outcome: string;
    tags: EvaluationTag[];
    outcomeIcon?: IconName | null;
    outcomeColor?: string | null;
  };
};

export type InvestigationViewAlert = Application & {
  alertReview: CompletedAlertReview | null;
  alertUnavailable?: boolean;
  assignableAgents?: InvestigationAgent[];
  assignedAgent: InvestigationAgent | null;
  deviceIds?: string[];
  evaluationsByToken: EvaluationsByToken;
  eventType: string | null;
  journeyAlertOutcomeReasons: TagEvidenceResponse[];
  journeyApplicationState: JourneyApplicationStateTypes;
  journeySteps: AlertJourneyStep[];
  outcomeOptionsByReviewNodeId: OutcomeOptionsByReviewNodeId;
  reasonOptionsByOutcomeId: ReasonOptionsByOutcomeId;
  sourceWorkflowType: string;
  additionalInvestigations: {
    investigation_token: string;
    created_at: string;
  }[];
};

export type InvestigationEntity = {
  entityToken: string;
  entityName: string;
  entityType: InvestigationEntityType;
  riskScore?: number;
  activeBusinessVersionId?: number | null;
  activePersonVersionId?: number | null;
  customerPartnerId?: number | null;
  externalEntityIdentifier?: any;
  externalIngestionIdentifier?: any;
  meta?: any;
  recentAction?: any;
  entityGroupRelationships?: Array<{ entityToken: string; entityName?: string }>;
};

export type InvestigationEntityMap = {
  [key: string]: InvestigationEntity;
};

export type InvestigationAgent = {
  firstName: string;
  lastName: string;
  email: string;
  token: string;
  organizationNameSlug?: string;
  organizationName?: string;
  subGroup?: string;
};

export type InvestigationJourneyAlert = {
  journeyApplicationToken: string;
  journeyToken: string;
};

export type InvestigationEventRequest = {
  eventRequestToken: string;
  eventsApiTypeTitle: string;
};

export type InvestigationTag = {
  tagColor: string;
  tagName: string;
  isOutcomeReason: boolean;
  addedBy: InvestigationAgent;
  createdAt: number;
  investigationNoteId: number;
};

export type InvestigationEvidences = {
  journeyAlerts: InvestigationJourneyAlert[];
  tags: InvestigationTag[];
};

export type InvestigationDocument = {
  documentToken: string;
  type: string;
  name: string;
  extension: string;
  uploaded: string;
  timestamp: string;
  parentDocumentId: string;
  approvalTimestamp: string;
};

export type InvestigationEvent = {
  agent?: InvestigationAgent;
  assignedAgent?: InvestigationAgent;
  event: string;
  meta?: any;
  note?: string;
  timestamp?: string;
};

export type InvestigationNote = {
  createdAt: number;
  createdBy: InvestigationAgent;
  entityToken: string;
  entityName: string;
  text: string;
  isInvestigationLevelNote: boolean;
  tags: InvestigationTag[];
};

export type InvestigationResponseError = {
  code: number;
  message: string;
} | null;

export type InvestigationRow = {
  entityType: InvestigationEntityType;
  entityName: string;
  riskScore: number;
  token: string;
  type: string;
  createdAt: string;
  escalatedAt: string | null;
  closedAt: string;
  archivedAt: string | null;
  totalAlerts: number;
  updatedAt: string;
  assignee: string;
  assignees: string[];
  events: InvestigationEventRequest[];
  investigationOutcome?: string;
  investigationOutcomeColor?: string;
  investigationOutcomeIcon?: IconName;
  investigationStatusColor?: string;
  investigationStatusIcon?: IconName;
  investigationState: InvestigationStateTypes;
  investigationStatus?: string;
  organizationNameSlug?: string;
  organizationName?: string;
  tags: {
    tagName: string;
    tagColor: string;
    isOutcomeReason: boolean;
  }[];
};

export type SavedSearch = {
  created_at: string;
  creator: string;
  customer_id: number;
  customer_query_id: number;
  default_agents: any[];
  delete_link: any;
  description: string;
  description_fa: any;
  formatted_created_at: string;
  id: number;
  is_agent_default: boolean | null;
  is_archived: boolean;
  is_default: boolean;
  is_public: boolean;
  is_role_default: boolean | null;
  is_role_tab: boolean | null;
  name: string;
  owner_customer_agent_id: number | null;
  public: string;
  query_json: any;
  query_string: string;
  update_url: string;
  updated_at: string;
  url_path: string;
  view_link: any;
  shared_users: [];
};

export enum InvestigationTableSortOrder {
  DESC = 'DESC',
  ASC = 'ASC',
}

export type SelectedInvestigations = {
  investigationStates: string[];
  investigationArchivedAt: boolean[];
  investigationTokens: string[];
  investigationTypes: string[];
};

export type InvestigationSummary = {
  alert_count: number;
  alert_types: string[];
  manual_evidences: {
    tag_color: string;
    tag_name: string;
    investigation_note_id: number;
  }[];
  outcome_reasons: {
    tag_color: string;
    tag_name: string;
  }[];
  pending_alert_count: number;
};

/**
 * Types for the Investigation Status Select component
 */
export type StatusItem = {
  label: string;
  value: string;
  isEscalated?: boolean;
  isNonEscalated?: boolean;
};
export type GroupedOption = { label: string | JSX.Element; options: SelectOption[] };
export type OptionData = { label?: string; value?: string };
export type InvestigationStatusItem = {
  label: string;
  isEscalated?: boolean;
  isNonEscalated?: boolean;
  outcome_color?: string;
  icon_name?: string;
};
